import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
  Grid,
  Tabs,
  Tab,
  Alert,
  CircularProgress
} from '@mui/material';
import { 
  EmojiEvents, 
  Group, 
  Timeline,
  ArrowBack,
  Flag,
  People,
  Delete
} from '@mui/icons-material';
import challengesApi from '../../services/challengesApi';
import ActivityChart from '../Dashboard/ActivityChart';

const ChallengeDetails = ({ challengeId, onClose }) => {
  const [challenge, setChallenge] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [dailyProgress, setDailyProgress] = useState([]);
  const [teamStats, setTeamStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    // Get current user from Cognito
    const getCurrentUser = async () => {
      try {
        const { getCurrentUser } = await import('aws-amplify/auth');
        const user = await getCurrentUser();
        setCurrentUser(user.username);
      } catch (error) {
        console.error('Error getting current user:', error);
      }
    };
    getCurrentUser();
  }, []);

  useEffect(() => {
    const fetchChallengeData = async () => {
      if (!challengeId) return;
      
      setLoading(true);
      setError(null);
      
      try {
        const challengeDetails = await challengesApi.getChallengeDetails(challengeId);
        const challengeData = challengeDetails.challenge;
        
        // Use points leaderboard for points_sabotage challenges
        let leaderboardData;
        if (challengeData.challenge_type === 'points_sabotage') {
          console.log('Using points leaderboard for points_sabotage challenge');
          // Try to use points leaderboard API, fall back to regular if needed
          leaderboardData = await challengesApi.getPointsLeaderboard(challengeId).catch(async (error) => {
            console.error('Points leaderboard API error, falling back to regular:', error);
            return await challengesApi.getChallengeLeaderboard(challengeId).catch((fallbackError) => {
              console.error('Regular leaderboard API error:', fallbackError);
              return { participants: [] };
            });
          });
        } else {
          leaderboardData = await challengesApi.getChallengeLeaderboard(challengeId).catch((error) => {
            console.error('Regular leaderboard API error:', error);
            return { participants: [] };
          });
        }
        
        console.log('Challenge Details:', challengeDetails);
        console.log('Leaderboard Data:', leaderboardData);
        console.log('Challenge Type:', challengeData.challenge_type);
        console.log('Should show InteractiveLeaderboard?', challengeData.challenge_type === 'points_sabotage');
        
        setChallenge(challengeData);
        
        // Points leaderboard returns data in 'leaderboard' field, regular API might use 'participants'
        const participants = leaderboardData.leaderboard || leaderboardData.participants || [];
        console.log('Final participants:', participants);
        console.log('Expected format check - first participant:', participants[0]);
        
        // For points_sabotage challenges, ensure we're using the correct data format
        if (challengeData.challenge_type === 'points_sabotage' && leaderboardData.leaderboard) {
          console.log('Using points leaderboard data with', leaderboardData.leaderboard.length, 'participants');
          setLeaderboard(leaderboardData.leaderboard);
        } else {
          console.log('Using regular leaderboard data with', participants.length, 'participants');
          setLeaderboard(participants);
        }
        
        // Fetch additional data based on challenge type
        if (challengeData.challenge_type === 'team') {
          const teamData = await challengesApi.getTeamStats(challengeId).catch(() => null);
          setTeamStats(teamData);
        }
        
        // Fetch daily progress
        const progressData = await challengesApi.getDailyProgress(challengeId).catch(() => []);
        setDailyProgress(progressData.daily_progress || progressData || []);
        
      } catch (error) {
        console.error('Error fetching challenge data:', error);
        setError(error.message || 'Failed to load challenge details');
      } finally {
        setLoading(false);
      }
    };

    fetchChallengeData();
  }, [challengeId]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleDeleteChallenge = async () => {
    if (!window.confirm('Are you sure you want to delete this challenge? This action cannot be undone.')) {
      return;
    }

    try {
      await challengesApi.deleteChallenge(challengeId);
      onClose(); // Return to challenges list
    } catch (error) {
      console.error('Error deleting challenge:', error);
      setError('Failed to delete challenge. Please try again.');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getRankSuffix = (rank) => {
    if (rank === 1) return 'st';
    if (rank === 2) return 'nd';
    if (rank === 3) return 'rd';
    return 'th';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Loading challenge details...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Button 
          startIcon={<ArrowBack />} 
          onClick={onClose} 
          sx={{ mb: 2 }}
        >
          Back to Challenges
        </Button>
        <Alert severity="error">
          {error}
        </Alert>
      </Box>
    );
  }

  if (!challenge) {
    return (
      <Box>
        <Button 
          startIcon={<ArrowBack />} 
          onClick={onClose} 
          sx={{ mb: 2 }}
        >
          Back to Challenges
        </Button>
        <Alert severity="info">
          Challenge not found
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Button 
            startIcon={<ArrowBack />} 
            onClick={onClose}
          >
            Back to Challenges
          </Button>
          
          {/* Delete button for challenge owners */}
          {currentUser && challenge.creator_id === currentUser && (
            <Button
              color="error"
              variant="outlined"
              startIcon={<Delete />}
              onClick={handleDeleteChallenge}
            >
              Delete Challenge
            </Button>
          )}
        </Box>
        
        <Box display="flex" alignItems="center" gap={2} mb={2}>
          <Avatar 
            sx={{ 
              width: 60, 
              height: 60, 
              bgcolor: challenge.challenge_type === 'team' ? 'primary.main' : 'secondary.main' 
            }}
          >
            {challenge.challenge_type === 'team' ? <Group /> : <EmojiEvents />}
          </Avatar>
          <Box>
            <Typography variant="h4" gutterBottom>
              {challenge.title || challenge.name}
            </Typography>
            <Box display="flex" gap={1}>
              <Chip label={challenge.status || 'Active'} color="success" />
              <Chip label={challenge.challenge_type || 'Individual'} variant="outlined" />
              <Chip 
                label={`${challenge.participant_count || 0} participants`} 
                variant="outlined" 
                icon={<People />}
              />
            </Box>
          </Box>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Main Content */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              {/* Description */}
              <Typography variant="h6" gutterBottom>
                Challenge Description
              </Typography>
              <Typography variant="body1" paragraph>
                {challenge.description || 'No description available for this challenge.'}
              </Typography>

              {/* Challenge Info */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Start Date
                  </Typography>
                  <Typography variant="body1">
                    {formatDate(challenge.start_date)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    End Date
                  </Typography>
                  <Typography variant="body1">
                    {formatDate(challenge.end_date)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Daily Goal
                  </Typography>
                  <Typography variant="body1">
                    {challenge.daily_goal?.toLocaleString() || '10,000'} steps
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Challenge Type
                  </Typography>
                  <Typography variant="body1" sx={{ textTransform: 'capitalize' }}>
                    {challenge.challenge_type || 'Individual'}
                  </Typography>
                </Grid>
              </Grid>

              {/* User Progress */}
              {challenge.user_progress !== undefined && (
                <Box mb={3}>
                  <Typography variant="h6" gutterBottom>
                    Your Progress
                  </Typography>
                  <Box display="flex" alignItems="center" gap={2} mb={1}>
                    <LinearProgress 
                      variant="determinate" 
                      value={Math.min(challenge.user_progress || 0, 100)} 
                      sx={{ flexGrow: 1, height: 10, borderRadius: 5 }}
                    />
                    <Typography variant="body2" fontWeight="medium">
                      {Math.round(challenge.user_progress || 0)}%
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {challenge.user_steps?.toLocaleString() || '0'} steps completed
                  </Typography>
                </Box>
              )}

              {/* Tabs for detailed views */}
              <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 3 }}>
                <Tab label="Progress Chart" icon={<Timeline />} iconPosition="start" />
                {challenge.challenge_type === 'team' && (
                  <Tab label="Team Stats" icon={<Group />} iconPosition="start" />
                )}
              </Tabs>

              {/* Tab Content */}
              {activeTab === 0 && (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Daily Progress
                  </Typography>
                  {dailyProgress.length > 0 ? (
                    <ActivityChart 
                      data={dailyProgress} 
                      loading={false}
                      chartType="line"
                    />
                  ) : (
                    <Alert severity="info">
                      No progress data available yet. Start logging your steps to see your progress!
                    </Alert>
                  )}
                </Box>
              )}

              {activeTab === 1 && challenge.challenge_type === 'team' && (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Team Statistics
                  </Typography>
                  {teamStats ? (
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Card variant="outlined">
                          <CardContent>
                            <Typography variant="h4" color="primary.main">
                              {teamStats.total_steps?.toLocaleString() || '0'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Total Team Steps
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Card variant="outlined">
                          <CardContent>
                            <Typography variant="h4" color="secondary.main">
                              {teamStats.average_steps?.toLocaleString() || '0'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Average Steps per Member
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    </Grid>
                  ) : (
                    <Alert severity="info">
                      Team statistics are not available yet.
                    </Alert>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Sidebar - Leaderboard */}
        <Grid item xs={12} md={4}>
          {challenge.challenge_type === 'points_sabotage' ? (
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom display="flex" alignItems="center" gap={1}>
                  <EmojiEvents />
                  Interactive Leaderboard
                </Typography>
                
                <Alert severity="info" sx={{ mb: 2 }}>
                  This is a points_sabotage challenge. Interactive features will be available here.
                </Alert>
                
                {leaderboard.length > 0 ? (
                  <List disablePadding>
                    {leaderboard.slice(0, 10).map((participant, index) => (
                      <React.Fragment key={participant.user_id || participant.id || index}>
                        <ListItem sx={{ px: 0 }}>
                          <ListItemAvatar>
                            <Avatar 
                              sx={{ 
                                bgcolor: index < 3 ? 'primary.main' : 'grey.300',
                                color: index < 3 ? 'white' : 'text.primary',
                                fontWeight: 'bold'
                              }}
                            >
                              {index + 1}
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              <Box display="flex" justifyContent="space-between" alignItems="center">
                                <Typography variant="body2" fontWeight="medium">
                                  {participant.display_name || participant.profile?.display_name || participant.name || participant.username || `User ${index + 1}`}
                                </Typography>
                                <Typography variant="h6" color="primary.main" fontWeight="bold">
                                  {participant.total_points || 0} points
                                </Typography>
                              </Box>
                            }
                            secondary={
                              <Box display="flex" justifyContent="space-between" alignItems="center" mt={1}>
                                <Typography variant="caption" color="text.secondary">
                                  Streak: {participant.streak_status?.current || 0} days
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                  <Button size="small" variant="outlined" color="error">-1</Button>
                                  <Button size="small" variant="outlined" color="success">+1</Button>
                                  <Button size="small" variant="outlined" color="success">+5</Button>
                                  <Button size="small" variant="outlined" color="success">+10</Button>
                                </Box>
                              </Box>
                            }
                          />
                        </ListItem>
                        {index < leaderboard.length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
                    No participants yet
                  </Typography>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom display="flex" alignItems="center" gap={1}>
                  <Flag />
                  Leaderboard
                </Typography>
                
                {leaderboard.length > 0 ? (
                  <List disablePadding>
                    {leaderboard.slice(0, 10).map((participant, index) => (
                      <React.Fragment key={participant.user_id || participant.id || index}>
                        <ListItem sx={{ px: 0 }}>
                          <ListItemAvatar>
                            <Avatar 
                              sx={{ 
                                bgcolor: index < 3 ? 'primary.main' : 'grey.300',
                                color: index < 3 ? 'white' : 'text.primary',
                                fontWeight: 'bold'
                              }}
                            >
                              {index + 1}
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              <Box display="flex" justifyContent="space-between" alignItems="center">
                                <Typography variant="body2" fontWeight="medium">
                                  {participant.display_name || participant.profile?.display_name || participant.name || participant.username || `User ${index + 1}`}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {index + 1}{getRankSuffix(index + 1)}
                                </Typography>
                              </Box>
                            }
                            secondary={
                              <Box>
                                <Typography variant="body2" color="primary.main">
                                  {participant.total_points || participant.total_steps?.toLocaleString() || participant.steps?.toLocaleString() || '0'} 
                                  {challenge.challenge_type === 'points_sabotage' ? ' points' : ' steps'}
                                </Typography>
                                {participant.progress !== undefined && (
                                  <LinearProgress 
                                    variant="determinate" 
                                    value={Math.min(participant.progress || 0, 100)}
                                    size="small"
                                    sx={{ mt: 0.5, height: 4 }}
                                  />
                                )}
                              </Box>
                            }
                          />
                        </ListItem>
                        {index < leaderboard.length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
                    No participants yet
                  </Typography>
                )}
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default ChallengeDetails;